// ═══════════════════════════════════════════════════
//  Component CNC Cost — Cria Library pricing
//
//  MIRROR of packages/server/src/modules/nesting/helpers/cnc-costs.ts, which
//  stays the source of truth for project estimates. This copy exists so the
//  library component modals can price CNC live and client-side, the same way
//  banquette-cost.ts mirrors its server helper. Any formula change there must
//  be applied here; the golden-fixture test is the drift guard.
//
//  The one deliberate difference: setup, sheet handling, and tooling are
//  amortized over a reference batch quantity N. Those three are per-run or
//  per-sheet costs. They are correct at project scale, where many line items
//  pool onto shared sheets, but a lone component would absorb a whole setup
//  charge and one mostly-empty sheet.
//
//  Divide-by-N is applied to the whole result rather than to selected buckets.
//  That is safe because cutting, rapid, and cleanup are linear in N — dividing
//  them by N returns their true per-unit value unchanged. Only the three
//  non-linear buckets actually move, which is the intended correction. The
//  linearity property is pinned by a test so it stays true.
// ═══════════════════════════════════════════════════

import type { MaterialLibrary } from '../schemas/material-library.schema';
import { packComponentParts, type ComponentNestingPart } from './component-nesting';

/** Matches the rapid-repositioning assumption in cnc-costs.ts. */
const RAPID_MOVE_FRACTION = 0.2;

export interface ComponentCncSettings {
  cnc_enabled: boolean;
  cnc_feed_rate: number;             // in/min
  cnc_rapid_rate: number;            // in/min
  cnc_sheet_load_time: number;       // min per sheet
  cnc_sheet_unload_time: number;     // min per sheet
  cnc_part_cleanup_time: number;     // min per part
  cnc_setup_time: number;            // min per material per run
  cnc_operator_rate: number;         // $/hr
  cnc_machine_overhead_rate: number; // $/hr
  cnc_bit_price: number;             // $
  cnc_sheets_per_bit: number;
  cnc_reference_batch_qty: number;
}

export interface ComponentCncMaterialCost {
  material_id: string;
  material_name: string;
  /** Figures at the reference batch of N units. */
  sheets: number;
  parts: number;
  cut_inches: number;
  cutting_min: number;
  rapid_min: number;
  handling_min: number;
  cleanup_min: number;
  setup_min: number;
  total_min: number;
  operator_cost: number;
  machine_cost: number;
  tooling_cost: number;
  total_cost: number;
  /** Batch figures divided by N. */
  min_per_unit: number;
  cost_per_unit: number;
  sheets_per_unit: number;
}

export interface ComponentCncResult {
  /** Always present so the number is self-describing in the UI. */
  batch_qty: number;
  enabled: boolean;
  materials: ComponentCncMaterialCost[];
  total_min_per_unit: number;
  total_cost_per_unit: number;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function emptyResult(batchQty: number): ComponentCncResult {
  return {
    batch_qty: batchQty,
    enabled: false,
    materials: [],
    total_min_per_unit: 0,
    total_cost_per_unit: 0,
  };
}

/**
 * Price the CNC time for ONE unit of a component.
 *
 * @param parts             rectangles for one unit, as emitted by the cost utils
 * @param materials         material lookup
 * @param settings          company CNC settings; undefined before they load
 * @param companyKerf       fallback kerf when a material has no nesting_spacing
 * @param batchQtyOverride  diagnostic use only — lets the nesting panel show
 *                          what the cost would be at the quantity being viewed.
 *                          The pricing path never passes this.
 */
export function calcComponentCncCost(
  parts: ComponentNestingPart[],
  materials: Map<string, MaterialLibrary>,
  settings: ComponentCncSettings | undefined,
  companyKerf?: number | null,
  batchQtyOverride?: number,
): ComponentCncResult {
  if (!settings || !settings.cnc_enabled) return emptyResult(1);

  const raw = batchQtyOverride ?? settings.cnc_reference_batch_qty;
  const n = Math.max(1, Math.floor(Number.isFinite(raw) ? raw : 10));

  const nested = packComponentParts(parts, materials, n, companyKerf);

  const out: ComponentCncMaterialCost[] = [];
  let totalMinPerUnit = 0;
  let totalCostPerUnit = 0;

  for (const nest of nested) {
    // Mirrors the totalSheets === 0 guard in calcCncCosts(); this also drops
    // the non-sheet rows, which are bought by area or length and never nested.
    if (nest.sheet_count === 0) continue;

    // Not every nested material is router-cut. Foam is nested for yield but is
    // cut by hand or on a different machine.
    const mat = materials.get(nest.material_id);
    if (mat && mat.cnc_cut === false) continue;

    let cutInches = 0;
    let partCount = 0;
    for (const sheet of nest.sheets) {
      for (const p of sheet.placements) {
        cutInches += 2 * (p.width + p.height);
        partCount++;
      }
    }

    const cuttingMin = settings.cnc_feed_rate > 0 ? cutInches / settings.cnc_feed_rate : 0;
    const rapidMin = settings.cnc_rapid_rate > 0
      ? (cutInches * RAPID_MOVE_FRACTION) / settings.cnc_rapid_rate
      : 0;
    const handlingMin = nest.sheet_count
      * (settings.cnc_sheet_load_time + settings.cnc_sheet_unload_time);
    const cleanupMin = partCount * settings.cnc_part_cleanup_time;
    const setupMin = settings.cnc_setup_time;
    const totalMin = cuttingMin + rapidMin + handlingMin + cleanupMin + setupMin;

    const totalHr = totalMin / 60;
    const operatorCost = totalHr * settings.cnc_operator_rate;
    const machineCost = totalHr * settings.cnc_machine_overhead_rate;
    const toolingCost = settings.cnc_sheets_per_bit > 0
      ? (nest.sheet_count / settings.cnc_sheets_per_bit) * settings.cnc_bit_price
      : 0;
    const totalCost = operatorCost + machineCost + toolingCost;

    out.push({
      material_id: nest.material_id,
      material_name: nest.material_name,
      sheets: nest.sheet_count,
      parts: partCount,
      cut_inches: round2(cutInches),
      cutting_min: round2(cuttingMin),
      rapid_min: round2(rapidMin),
      handling_min: round2(handlingMin),
      cleanup_min: round2(cleanupMin),
      setup_min: round2(setupMin),
      total_min: round2(totalMin),
      operator_cost: round2(operatorCost),
      machine_cost: round2(machineCost),
      tooling_cost: round2(toolingCost),
      total_cost: round2(totalCost),
      min_per_unit: round2(totalMin / n),
      cost_per_unit: round2(totalCost / n),
      sheets_per_unit: round2(nest.sheet_count / n),
    });

    // Accumulate unrounded, round once at the end.
    totalMinPerUnit += totalMin / n;
    totalCostPerUnit += totalCost / n;
  }

  return {
    batch_qty: n,
    enabled: true,
    materials: out,
    total_min_per_unit: round2(totalMinPerUnit),
    total_cost_per_unit: round2(totalCostPerUnit),
  };
}
